import { useAppSelector } from "store/hooks";
import LoadingImage from "components/LoadingImage";
import { ChangeEvent, useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { APIGetMentors, AVATARGetUserAvatarUrl, APIUpdateMentorByID, APIGetMentorServices, APIValidateMentorProfileID, APIGetMentorValueProposition, APIUploadMentorPicture } from "utils/api";
import { Link } from "@mui/material";
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import ImageCropProvider from "components/Cropper/ImageCropProvider";
import ProfileAvatar from "components/ProfileAvatar";
import MentorshipCard from "./MentorshipCard";
import PersonalInterestsSection from "../PersonalInterestsSection";
import ValuePropositionCard from "./ValuePropositionCard";
const LinkedInIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 44 44" fill="none">
  <path d="M38.5 33.9167C38.5 36.4485 36.4485 38.5 33.9167 38.5H10.0833C7.55242 38.5 5.5 36.4485 5.5 33.9167V10.0833C5.5 7.5515 7.55242 5.5 10.0833 5.5H33.9167C36.4485 5.5 38.5 7.5515 38.5 10.0833V33.9167Z" fill="#0288D1" />
  <path d="M11 17.4167H15.5833V33H11V17.4167ZM13.2779 15.5833H13.2522C11.8846 15.5833 11 14.564 11 13.2907C11 11.99 11.9121 11 13.3045 11C14.6988 11 15.5577 11.99 15.5833 13.2907C15.5833 14.5631 14.6987 15.5833 13.2779 15.5833ZM33 33H28.4167V24.6592C28.4167 22.6444 27.2938 21.2694 25.4907 21.2694C24.1148 21.2694 23.3704 22.1971 23.0092 23.0936C22.8772 23.4144 22.9167 24.3018 22.9167 24.75V33H18.3333V17.4167H22.9167V19.8147C23.5776 18.7917 24.6125 17.4167 27.2598 17.4167C30.5397 17.4167 32.9991 19.4792 32.9991 24.0845L33 33Z" fill="white" />
</svg>;
const GithubIcon = () => <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-[38px] h-[38px]"><path d="M12 0C5.373 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.385.599.111.793-.261.793-.577V17.44c-3.293.736-4.088-1.416-4.088-1.416-.545-1.387-1.332-1.756-1.332-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.237 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.184 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.661.242 2.883.118 3.184.77.838 1.235 1.91 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.818 2.224v3.293c0 .319.192.694.801.576C20.565 21.8 24 17.302 24 12 24 5.373 18.627 0 12 0z"></path></svg>;
const XIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="45" height="45" viewBox="0 0 45 45" fill="none">
  <path fill-rule="evenodd" clip-rule="evenodd" d="M35.625 39.375H9.375C7.30406 39.375 5.625 37.6959 5.625 35.625V9.375C5.625 7.30406 7.30406 5.625 9.375 5.625H35.625C37.6959 5.625 39.375 7.30406 39.375 9.375V35.625C39.375 37.6959 37.6959 39.375 35.625 39.375Z" fill="#212121" />
  <path fill-rule="evenodd" clip-rule="evenodd" d="M32.1161 31.875H26.0814L12.9648 13.125H18.9995L32.1161 31.875ZM26.8005 30.285H29.2033L18.2805 14.715H15.8777L26.8005 30.285Z" fill="white" />
  <path fill-rule="evenodd" clip-rule="evenodd" d="M14.8743 31.8763L21.6271 24.0538L20.744 22.8828L12.959 31.8763H14.8743Z" fill="white" />
  <path fill-rule="evenodd" clip-rule="evenodd" d="M22.9219 20.3634L23.7703 21.5719L31.065 13.125H29.19L22.9219 20.3634Z" fill="white" />
</svg>
const FacebookIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 44 44" fill="none">
  <path d="M40.25 35.4583C40.25 38.1053 38.1053 40.25 35.4583 40.25H10.5417C7.89571 40.25 5.75 38.1053 5.75 35.4583V10.5417C5.75 7.89475 7.89571 5.75 10.5417 5.75H35.4583C38.1053 5.75 40.25 7.89475 40.25 10.5417V35.4583Z" fill="#3F51B5" />
  <path d="M32.9363 23.957H29.7087V36.4154H24.917V23.957H22.042V20.1237H24.917V17.8141C24.9189 14.4523 26.3152 12.457 30.276 12.457H33.542V16.2904H31.3503C29.8083 16.2904 29.7087 16.8654 29.7087 17.9416V20.1237H33.542L32.9363 23.957Z" fill="white" />
</svg>;
const InstagramIcon = () => <img src={require("assets/instagram.svg").default} alt="Instagram" />;
const CheckmarkIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26" fill="none">
  <circle cx="13" cy="13" r="13" fill="#EAF3FF" />
  <path d="M6.38867 12.9366L11.1799 17.7873L19.1652 8.08594" fill="#EAF3FF" />
  <path d="M6.38867 12.9366L11.1799 17.7873L19.1652 8.08594" stroke="#407EC9" strokeWidth="0.808451" strokeLinecap="round" strokeLinejoin="round" />
</svg>
const CrossIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 26 26" fill="none">
  <g clipPath="url(#clip0_1498_4836)">
    <circle cx="13" cy="13" r="13" fill="#EF4444" />
    <path d="M9 9L17 17M9 17L17 9" stroke="white" strokeWidth="0.5" strokeLinecap="round" strokeLinejoin="round" />
  </g>
  <defs>
    <clipPath id="clip0_1498_4836">
      <rect width="26" height="26" fill="white" />
    </clipPath>
  </defs>
</svg>

const CustomToggle = ({ label, checked, onChange }) => {
  return (
    <div className="flex items-center">
      <label className="inline-flex relative items-center cursor-pointer">
        <input
          type="checkbox"
          className="sr-only peer"
          checked={checked}
          onChange={onChange}
        />
        <div className={`
              relative w-[40px] h-[20px]
              rounded-full
              bg-gradient-to-b from-[#B8B8B8] to-[#DCDCDC]
              shadow-sm
              peer-checked:bg-[#407EC9] peer-checked:from-[#407EC9] peer-checked:to-[#407EC9]
              transition-all duration-200
            `}></div>
        <div className={`absolute bg-gradient-to-b from-white to-gray-50 rounded-full shadow-sm transition-transform duration-200 transform left-[1px] top-[1px] w-[17px] h-[17px] peer-checked:translate-x-5`}></div>
        <span className="ml-[8px] text-sm font-medium text-gray-500">
          {label}
        </span>
      </label>
    </div>
  );
};
const DarkCustomToggle = ({ label, checked, onChange }) => {
  return (
    <div className="flex items-center">
      <label className="inline-flex relative items-center cursor-pointer">
        <input
          type="checkbox"
          className="sr-only peer"
          checked={checked}
          onChange={onChange}
        />
        <div className={`
              relative w-[40px] h-[20px]
              rounded-full
              bg-gradient-to-b from-[#B8B8B8] to-[#DCDCDC]
              shadow-sm
              peer-checked:bg-[#374151] peer-checked:from-[#374151] peer-checked:to-[#374151]
              transition-all duration-200
            `}></div>
        <div className={`absolute bg-gradient-to-b from-white to-gray-50 rounded-full shadow-sm transition-transform duration-200 transform left-[1px] top-[1px] w-[17px] h-[17px] peer-checked:translate-x-5`}></div>
        <span className="ml-[8px] text-sm font-medium text-gray-500">
          {label}
        </span>
      </label>
    </div>
  );
};

const isMentorProfileID = (profileId: string) => {
  if (!profileId) {
    return new Yup.ValidationError("Page ID is required");
  }
  if (profileId.length < 6) {
    return new Yup.ValidationError("Page ID must be at least 6 characters");
  }
  if (profileId.length > 40) {
    return new Yup.ValidationError("Page ID must be less than 40 characters");
  }
  if (!/^[a-zA-Z][a-zA-Z0-9]{5,39}$/.test(profileId)) {
    return new Yup.ValidationError("Page ID must start with a letter and contain only letters and numbers");
  }
  return true;
}


const EditMentorProfilePage = () => {
  const [bioCharCount, setBioCharCount] = useState(0);
  const user = useAppSelector((state) => state.auth.user_me);
  const isLoggedIn = useAppSelector((state) => state.auth.isLoggedIn);
  const navigate = useNavigate();

  const [showInviteMentors, setShowInviteMentors] = useState(true);
  const [showLegalInfo, setShowLegalInfo] = useState(true);
  const [showAddServiceForm, setShowAddServiceForm] = useState(false);
  const [showAddValuePropositionForm, setShowAddValuePropositionForm] = useState(false);

  const queryClient = useQueryClient();

  const [profileIdStatus, setProfileIdStatus] = useState<null | "checking" | "valid" | "taken" | "invalid" | "error">(null);
  const [profileIdValue, setProfileIdValue] = useState("");
  const [profileIdTimeout, setProfileIdTimeout] = useState<NodeJS.Timeout | null>(null);

  const mentorQuery = useQuery({
    queryKey: ["mentor_me"],
    queryFn: () => APIGetMentors(null, true, user.id).then((res) => res.data[0] || null),
    enabled: isLoggedIn,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const mentorServicesQuery = useQuery({
    queryKey: ['mentorServices', mentorQuery.data?.id],
    queryFn: () => APIGetMentorServices(mentorQuery.data?.id).then(res => res.data),
    enabled: !!(mentorQuery.isSuccess && mentorQuery.data?.id),
  });

  const mentorValuePropositionQuery = useQuery({
    queryKey: ['mentorValueProposition', mentorQuery.data?.id],
    queryFn: () => APIGetMentorValueProposition(mentorQuery.data?.id).then(res => res.data),
    enabled: !!(mentorQuery.isSuccess && mentorQuery.data?.id),
  });

  const validationSchema = Yup.object().shape({
    profile_id: Yup.string().required('Page ID is required').test('!isMentorProfileID', isMentorProfileID),
    lang: Yup.string().required('Language is required'),
    mentor_profile_title: Yup.string(),
    mentor_profile_tagline: Yup.string(),
    mentor_description: Yup.string().required('Bio is required').max(400, 'Bio cannot exceed 400 characters'),
    linkedinUrl: Yup.string().url('Invalid LinkedIn URL'),
    githubUrl: Yup.string().url('Invalid Github URL'),
    XUrl: Yup.string().url('Invalid X URL'),
    facebookUrl: Yup.string().url('Invalid Facebook URL'),
    instagramUrl: Yup.string().url('Invalid Instagram URL'),
    tags: Yup.array().of(Yup.string()),
  });

  const updateMentorMutation = useMutation({
    mutationFn: (payload: any) => {
      if (!mentorQuery.data?.id) {
        return Promise.reject(new Error("Mentor ID not found"));
      }

      return APIUpdateMentorByID(mentorQuery.data.id, payload);
    },
    onSuccess: (data) => {
      console.log("Mentor updated successfully:", data);
      queryClient.invalidateQueries({ queryKey: ['mentor_me'] });
      alert("Mentor updated successfully");
    },
    onError: (error) => {
      console.error("Error updating mentor:", error);
      alert("Error updating mentor");
    },
  });

  /**
   * Avatar
   */
  const [croppedImageFile, setCroppedImageFile] = useState(null);
  const [isAvatarLoading, setIsAvatarLoading] = useState(false);
  const [avatarError, setAvatarError] = useState("");

  const handleUploadAvatar = async () => {
    if (croppedImageFile) {
      setAvatarError("");
      setIsAvatarLoading(true);
      APIUploadMentorPicture(mentorQuery.data.id, croppedImageFile)
        .then((res) => {
          setAvatarError("");
          setIsAvatarLoading(false);
          setCroppedImageFile(null);
        })
        .catch((err) => {
          console.log(err);
          setAvatarError("something-went-wrong-please-try-again");
          setIsAvatarLoading(false);
        });
    }
  };

  useEffect(() => {
    if (mentorQuery.data) {
      setBioCharCount(mentorQuery.data.mentor_description?.length || 0);
      setProfileIdValue(mentorQuery.data.profile_id || "");
      // go to error 404 if mentor is not active
      if (!mentorQuery.data.is_active) {
        navigate("/error-404");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mentorQuery.data]);


  const handleBioChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value
    if (text.length <= 400) {
      setBioCharCount(text.length)
    }
  }

  const handleToggleAddServiceForm = () => {
    setShowAddServiceForm(prev => !prev);
  };

  const handleToggleAddValuePropositionForm = () => {
    setShowAddValuePropositionForm(prev => !prev);
  };

  const handleCancelService = (serviceId?: number) => {
    if (!serviceId) {
      setShowAddServiceForm(false);
    }
  };

  const initialValues = {
    profile_id: mentorQuery.data?.profile_id || "",
    lang: mentorQuery.data?.lang || 'en',
    mentor_profile_title: mentorQuery.data?.mentor_profile_title || "",
    mentor_profile_tagline: mentorQuery.data?.mentor_profile_tagline || "",
    mentor_description: mentorQuery.data?.mentor_description || "",
    linkedinUrl: mentorQuery.data?.socials?.find(s => s.platform === 'linkedin')?.url || "",
    showLinkedin: mentorQuery.data?.socials?.find(s => s.platform === 'linkedin')?.visible ?? false,
    githubUrl: mentorQuery.data?.socials?.find(s => s.platform === 'github')?.url || "",
    showGithub: mentorQuery.data?.socials?.find(s => s.platform === 'github')?.visible ?? false,
    xUrl: mentorQuery.data?.socials?.find(s => s.platform === 'x')?.url || "",
    showX: mentorQuery.data?.socials?.find(s => s.platform === 'x')?.visible ?? false,
    facebookUrl: mentorQuery.data?.socials?.find(s => s.platform === 'facebook')?.url || "",
    showFacebook: mentorQuery.data?.socials?.find(s => s.platform === 'facebook')?.visible ?? false,
    instagramUrl: mentorQuery.data?.socials?.find(s => s.platform === 'instagram')?.url || "",
    showInstagram: mentorQuery.data?.socials?.find(s => s.platform === 'instagram')?.visible ?? false,
    tags: mentorQuery.data?.tags || [],
    feature_flag_testimonials: mentorQuery.data?.feature_flag_testimonials ?? true,
    feature_flag_mentorship_services: mentorQuery.data?.feature_flag_mentorship_services ?? true,
    feature_flag_value_proposition: mentorQuery.data?.feature_flag_value_proposition ?? true,
    feature_flag_lab_catalogue: mentorQuery.data?.feature_flag_lab_catalogue ?? true,
    feature_flag_refer_become_mentor: mentorQuery.data?.feature_flag_refer_become_mentor ?? true,
    feature_flag_legal: mentorQuery.data?.feature_flag_legal ?? true,
  };


  const handleFormSubmit = (values) => {

    const socialsPayload = [
      { platform: 'linkedin', url: values.linkedinUrl, visible: values.showLinkedin },
      { platform: 'github', url: values.githubUrl, visible: values.showGithub },
      { platform: 'x', url: values.xUrl, visible: values.showX },
      { platform: 'facebook', url: values.facebookUrl, visible: values.showFacebook },
      { platform: 'instagram', url: values.instagramUrl, visible: values.showInstagram },
    ].filter(social => social.url);

    const payload = {
      profile_id: values.profile_id,
      lang: values.lang,
      mentor_firstname: user.firstname,
      mentor_lastname: user.lastname,
      mentor_profile_title: values.mentor_profile_title,
      mentor_profile_tagline: values.mentor_profile_tagline,
      mentor_description: values.mentor_description,
      socials: socialsPayload,
      tags: values.tags,
      feature_flag_testimonials: values.feature_flag_testimonials,
      feature_flag_mentorship_services: values.feature_flag_mentorship_services,
      feature_flag_lab_catalogue: values.feature_flag_lab_catalogue,
      feature_flag_value_proposition: values.feature_flag_value_proposition,
      feature_flag_refer_become_mentor: values.feature_flag_refer_become_mentor,
      feature_flag_legal: values.feature_flag_legal,
      user_id: user.id,
      picture: user.avatar
    };

    console.log("API Payload:", payload);
    updateMentorMutation.mutate(payload);
  };

  useEffect(() => {
    if (profileIdTimeout) {
      clearTimeout(profileIdTimeout);
    }
    if (profileIdValue === mentorQuery.data?.profile_id) {
      setProfileIdStatus(null);
      return;
    }
    if (!isMentorProfileID(profileIdValue.trim())) {
      setProfileIdStatus("invalid");
      return;
    }

    setProfileIdStatus("checking");
    const timeout = setTimeout(async () => {
      try {
        const res = await APIValidateMentorProfileID(profileIdValue.trim());
        if (res.status === 200) {
          setProfileIdStatus("valid");
        }
      } catch (err: any) {
        if (err?.response?.status === 409) {
          setProfileIdStatus("taken");
        } else {
          setProfileIdStatus("invalid");
        }
      }
    }, 1000);

    setProfileIdTimeout(timeout);

    return () => {
      if (profileIdTimeout) {
        clearTimeout(profileIdTimeout);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileIdValue]);

  return (
    <section className="flex w-full h-full border-l-[1px] border-[#F5F5F5]">
      {
        mentorQuery.isLoading || !user || !mentorQuery.data || !mentorQuery.data.is_active ? (
          <LoadingImage />
        ) :
          (<>
            <div className="flex flex-col pt-[56px] px-[46px] h-full w-full">
              <div className="flex flex-col items-start justify-between mb-8 md:flex-row md:items-center text-[#111827]">
                <div>
                  <h1 className="custom-text-[volkhov-700-35px]">
                    Hello {user.firstname}
                  </h1>
                  <p className="custom-text-[poppins-400-18px]">
                    Update your Mentor page informations here !
                  </p>
                </div>
              </div>
              {(isLoggedIn && user && mentorQuery.data && mentorQuery.data.id) ? (
                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleFormSubmit}
                  enableReinitialize={true}
                >
                  {({ isSubmitting, setFieldValue, values, errors, touched }) => {
                    return (
                      <Form>
                        <div className="flex gap-4 justify-end mb-4">
                          <Link target="_blank" rel="noopener noreferrer" href={`${process.env.REACT_APP_LANDING_URL}/mentors/${mentorQuery.data.profile_id}`} className="flex items-center gap-2 rounded-lg border border-[#407EC9] !no-underline px-6 py-3 text-[#407EC9] transition-colors hover:bg-gray-50">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                            <span className="custom-text-[poppins-500-15px]">
                              Preview Page
                            </span>
                          </Link>
                          <button
                            type="submit"
                            className="rounded-lg bg-[#407EC9] px-6 py-3 text-white transition-colors custom-text-[poppins-500-15px] hover:bg-[#3569A7] disabled:opacity-50"
                            disabled={isSubmitting || updateMentorMutation.isPending}
                          >
                            <span className="flex gap-2 items-center">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M20 6 9 17l-5-5" />
                              </svg>
                              {isSubmitting || updateMentorMutation.isPending ? 'Saving...' : 'Save Changes'}
                            </span>
                          </button>
                        </div>

                        <div className="grid grid-cols-1 gap-8 md:grid-cols-[2fr_1fr]">
                          <div className="space-y-6">
                            <div>
                              <label
                                htmlFor="profile_id"
                                className="mb-2 block text-lg custom-text-[poppins-500-16px] text-[#111827]"
                              >
                                Edit your Page ID
                              </label>
                              <div className="flex relative items-center pr-[10px]">
                                <div className={`flex items-center w-full rounded-[15px] px-4 py-3 shadow-[0px_7px_29px_0px_rgba(100,100,111,0.20)] bg-white focus-within:ring-2 focus-within:ring-[#407EC9] ${errors.profile_id && touched.profile_id ? 'ring-2 ring-red-500' : ''}`}>
                                  <span className="text-gray-500 custom-text-[poppins-400-16px]">
                                    https://scalyz.com/mentors/
                                  </span>
                                  <Field
                                    id="profile_id"
                                    name="profile_id"
                                    type="text"
                                    placeholder="your-unique-id"
                                    className="flex-grow ml-1 bg-transparent border-none outline-none custom-text-[poppins-400-16px] text-[#407EC9]"
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                      const value = e.target.value;
                                      setFieldValue("profile_id", value);
                                      setProfileIdValue(value);
                                    }}
                                    // add a checkmark or cross icon next to the input
                                    rightIcon={profileIdStatus === "valid" ? <CheckmarkIcon /> : null}
                                  />
                                </div>
                                {touched.profile_id && (errors.profile_id || profileIdStatus === "taken" || profileIdStatus === "error" || profileIdStatus === "invalid") ? <div className="ml-[15px]">
                                  <CrossIcon />
                                </div> : null}
                              </div>
                              {
                                touched.profile_id && !errors.profile_id && (
                                  <ErrorMessage name="profile_id" component="p" className="mt-1 text-sm text-red-500" />
                                )
                              }
                              {
                                touched.profile_id && !errors.profile_id && (
                                  <>
                                    {profileIdStatus === "checking" && <p className="mt-1 text-sm  custom-text-[poppins-400-14px] text-right mr-[27px] text-gray-500">Checking availability...</p>}
                                    {profileIdStatus === "valid" && <p className="mt-1 text-sm  custom-text-[poppins-400-14px] text-right mr-[27px] text-green-600">Profile ID is available</p>}
                                    {profileIdStatus === "taken" && <p className="mt-1 text-sm  custom-text-[poppins-400-14px] text-right mr-[27px] text-red-500">Profile ID is already taken</p>}
                                    {profileIdStatus === "error" && <p className="mt-1 text-sm  custom-text-[poppins-400-14px] text-right mr-[27px] text-red-500">Error checking profile ID</p>}
                                    {profileIdStatus === "invalid" && <p className="mt-1 text-sm  custom-text-[poppins-400-14px] text-right mr-[27px] text-red-500">Invalid profile ID</p>}
                                  </>
                                )
                              }

                            </div>

                            <div>
                              <h2 className="mb-4 custom-text-[poppins-500-16px] text-[#111827]">
                                Start by choosing the page language
                              </h2>
                              <div className="inline-flex overflow-hidden rounded-[15px] shadow-[0px_7px_29px_0px_rgba(100,100,111,0.20)]">
                                <button
                                  type="button"
                                  className={`px-8 py-4 border-r-[#6b728034] border-r-[1px] ${values.lang === "en" ? "bg-white custom-text-[poppins-600-16px] text-[#407EC9]" : "bg-gray-50 text-[#6B7280]"}`}
                                  onClick={() => setFieldValue("lang", "en")}
                                >
                                  English
                                </button>
                                <button
                                  type="button"
                                  className={`px-8 py-4 ${values.lang === "fr" ? "bg-white custom-text-[poppins-600-16px] text-[#407EC9]" : "bg-gray-50 text-[#6B7280]"}`}
                                  onClick={() => setFieldValue("lang", "fr")}
                                >
                                  French
                                </button>
                              </div>
                              <ErrorMessage name="lang" component="p" className="mt-1 text-sm text-red-500" />
                            </div>
                            <div>
                              <label
                                htmlFor="mentor_profile_title"
                                className="mb-2 block text-lg custom-text-[poppins-500-16px] text-[#111827]"
                              >
                                Title
                              </label>
                              <Field
                                id="mentor_profile_title"
                                name="mentor_profile_title"
                                type="text"
                                placeholder="Faisons connaissance...."
                                className={`w-full rounded-[15px] px-4 py-3 focus:ring-2 focus:ring-[#407EC9] shadow-[0px_7px_29px_0px_rgba(100,100,111,0.20)] border-none outline-none bg-white ${errors.mentor_profile_title && touched.mentor_profile_title ? 'ring-2 ring-red-500' : ''}`}
                              />
                              <ErrorMessage name="mentor_profile_title" component="p" className="mt-1 text-sm text-red-500" />
                            </div>

                            <div>
                              <label
                                htmlFor="mentor_profile_tagline"
                                className="mb-2 block text-lg custom-text-[poppins-500-16px] text-[#111827]"
                              >
                                Tagline
                              </label>
                              <Field
                                id="mentor_profile_tagline"
                                name="mentor_profile_tagline"
                                type="text"
                                placeholder="Découvre plus de détails sur moi en bas"
                                className={`w-full rounded-[15px] px-4 py-3 focus:ring-2 focus:ring-[#407EC9] shadow-[0px_7px_29px_0px_rgba(100,100,111,0.20)] border-none outline-none bg-white ${errors.mentor_profile_tagline && touched.mentor_profile_tagline ? 'ring-2 ring-red-500' : ''}`}
                              />
                              <ErrorMessage name="mentor_profile_tagline" component="p" className="mt-1 text-sm text-red-500" />
                            </div>

                            <div>
                              <label
                                htmlFor="mentor_description"
                                className="mb-2 block text-lg custom-text-[poppins-500-16px] text-[#111827]"
                              >
                                Bio
                              </label>
                              <Field
                                id="mentor_description"
                                name="mentor_description"
                                as="textarea"
                                onChange={(e) => {
                                  setFieldValue('mentor_description', e.target.value);
                                  handleBioChange(e);
                                }}
                                placeholder="Your bio here...."
                                rows={8}
                                className={`w-full rounded-[15px] px-4 py-3 focus:ring-2 focus:ring-[#407EC9] shadow-[0px_7px_29px_0px_rgba(100,100,111,0.20)] border-none outline-none bg-white ${errors.mentor_description && touched.mentor_description ? 'ring-2 ring-red-500' : ''}`}
                              />
                              <div className="flex justify-between mt-2">
                                {bioCharCount > 400 ? (
                                  <p className="text-sm text-red-500">
                                    ops you have reached the maximum number of
                                    characters
                                  </p>
                                ) : (
                                  <p
                                    className={
                                      bioCharCount > 350
                                        ? "text-sm text-yellow-500"
                                        : "text-sm text-gray-400"
                                    }
                                  >
                                    {bioCharCount > 350 &&
                                      "approaching character limit"}
                                  </p>
                                )}
                                <p className="text-sm text-gray-500">
                                  {bioCharCount}/400
                                </p>
                              </div>
                              <ErrorMessage name="mentor_description" component="p" className="mt-1 text-sm text-red-500" />
                            </div>
                          </div>

                          <div>
                            <label className="mb-4 block text-lg font-medium text-[#111827]">
                              Profile Photo
                            </label>
                            <div className="flex justify-center items-center">
                              <ImageCropProvider>
                                <ProfileAvatar
                                  croppedImageFile={croppedImageFile}
                                  handleUploadAvatar={handleUploadAvatar}
                                  isLoading={isAvatarLoading}
                                  error={avatarError}
                                  setCroppedImageFile={setCroppedImageFile}
                                  user_avatar={AVATARGetUserAvatarUrl(user)}
                                  user_fullname={`${user.firstname} ${user.lastname}`}
                                />
                              </ImageCropProvider>
                            </div>
                          </div>
                        </div>

                        <section className="mt-[69px] flex flex-col">
                          <div className="flex flex-row items-center justify-between gap-[17px] mb-4">
                            <h2 className="ml-[10px] custom-text-[poppins-600-32px] text-[#141414]">
                              Social Links
                            </h2>
                          </div>
                          <div className="w-full border-b-solid border-b-[1px] border-b-[rgba(0,0,0,0.25)] mb-6"></div>
                          <div className="ml-[10px] space-y-[27px]">
                            <div className="flex flex-col gap-[13px]">
                              <label className="w-24 custom-text-[poppins-500-16px] text-[#111827]">Linkedin</label>
                              <div className="flex flex-grow gap-[36px]">
                                <div className={`flex-grow flex items-center rounded-[15px] px-4 py-3 shadow-[0px_7px_29px_0px_rgba(100,100,111,0.20)] bg-white ${errors.linkedinUrl && touched.linkedinUrl ? 'ring-2 ring-red-500' : ''}`}>
                                  <LinkedInIcon />
                                  <Field
                                    name="linkedinUrl"
                                    type="text"
                                    placeholder="https://linkedin.com/in/..."
                                    className="flex-grow ml-3 bg-transparent border-none outline-none"
                                  />
                                </div>
                                <CustomToggle
                                  label={values.showLinkedin ? "Show" : "Hide"}
                                  checked={values.showLinkedin}
                                  onChange={() => setFieldValue("showLinkedin", !values.showLinkedin)}
                                />
                              </div>
                              <ErrorMessage name="linkedinUrl" component="p" className="mt-1 text-sm text-red-500" />
                            </div>
                            <div className="flex flex-col gap-[13px]">
                              <label className="w-24 custom-text-[poppins-500-16px] text-[#111827]">Github</label>
                              <div className="flex flex-grow gap-[36px]">
                                <div className={`flex-grow flex items-center rounded-[15px] px-4 py-3 shadow-[0px_7px_29px_0px_rgba(100,100,111,0.20)] bg-white ${errors.githubUrl && touched.githubUrl ? 'ring-2 ring-red-500' : ''}`}>
                                  <GithubIcon />
                                  <Field
                                    name="githubUrl"
                                    type="text"
                                    placeholder="https://github.com/..."
                                    className="flex-grow ml-3 bg-transparent border-none outline-none"
                                  />
                                </div>
                                <CustomToggle
                                  label={values.showGithub ? "Show" : "Hide"}
                                  checked={values.showGithub}
                                  onChange={() => setFieldValue("showGithub", !values.showGithub)}
                                />
                              </div>
                              <ErrorMessage name="githubUrl" component="p" className="mt-1 text-sm text-red-500" />
                            </div>
                            <div className="flex flex-col gap-[13px]">
                              <label className="w-24 custom-text-[poppins-500-16px] text-[#111827]">X</label>
                              <div className="flex flex-grow gap-[36px]">
                                <div className={`flex-grow flex items-center rounded-[15px] px-4 py-3 shadow-[0px_7px_29px_0px_rgba(100,100,111,0.20)] bg-white ${errors.xUrl && touched.xUrl ? 'ring-2 ring-red-500' : ''}`}>
                                  <XIcon />
                                  <Field
                                    name="xUrl"
                                    type="text"
                                    placeholder="https://x.com/..."
                                    className="flex-grow ml-3 bg-transparent border-none outline-none"
                                  />
                                </div>
                                <CustomToggle
                                  label={values.showX ? "Show" : "Hide"}
                                  checked={values.showX}
                                  onChange={() => setFieldValue("showX", !values.showX)}
                                />
                              </div>
                              <ErrorMessage name="xUrl" component="p" className="mt-1 text-sm text-red-500" />
                            </div>
                            <div className="flex flex-col gap-[13px]">
                              <label className="w-24 custom-text-[poppins-500-16px] text-[#111827]">Facebook</label>
                              <div className="flex flex-grow gap-[36px]">
                                <div className={`flex-grow flex items-center rounded-[15px] px-4 py-3 shadow-[0px_7px_29px_0px_rgba(100,100,111,0.20)] bg-white ${errors.facebookUrl && touched.facebookUrl ? 'ring-2 ring-red-500' : ''}`}>
                                  <FacebookIcon />
                                  <Field
                                    name="facebookUrl"
                                    type="text"
                                    placeholder="https://facebook.com/..."
                                    className="flex-grow ml-3 bg-transparent border-none outline-none"
                                  />
                                </div>
                                <CustomToggle
                                  label={values.showFacebook ? "Show" : "Hide"}
                                  checked={values.showFacebook}
                                  onChange={() => setFieldValue("showFacebook", !values.showFacebook)}
                                />
                              </div>
                              <ErrorMessage name="facebookUrl" component="p" className="mt-1 text-sm text-red-500" />
                            </div>
                            <div className="flex flex-col gap-[13px]">
                              <label className="w-24 custom-text-[poppins-500-16px] text-[#111827]">Instagram</label>
                              <div className="flex flex-grow gap-[36px]">
                                <div className={`flex-grow flex items-center rounded-[15px] px-4 py-3 shadow-[0px_7px_29px_0px_rgba(100,100,111,0.20)] bg-white ${errors.instagramUrl && touched.instagramUrl ? 'ring-2 ring-red-500' : ''}`}>
                                  <InstagramIcon />
                                  <Field
                                    name="instagramUrl"
                                    type="text"
                                    placeholder="https://instagram.com/..."
                                    className="flex-grow ml-3 bg-transparent border-none outline-none"
                                  />
                                </div>
                                <CustomToggle
                                  label={values.showInstagram ? "Show" : "Hide"}
                                  checked={values.showInstagram}
                                  onChange={() => setFieldValue("showInstagram", !values.showInstagram)}
                                />
                              </div>
                              <ErrorMessage name="instagramUrl" component="p" className="mt-1 text-sm text-red-500" />
                            </div>
                          </div>
                        </section>

                        <section className="mt-[69px] flex flex-col">
                          <div className="ml-[10px] flex flex-col w-full mt-[21px] gap-[24px]">
                            <PersonalInterestsSection
                              isLoading={false}
                              hasSubmitSuccess={false}
                              hasSubmitError={false}
                              handleUpdateTags={null}
                              tags={values.tags}
                              setTags={(newTags) => setFieldValue('tags', newTags)}
                              showUpdateButton={false}
                            />
                            <ErrorMessage name="tags" component="p" className="mt-1 text-sm text-red-500" />
                          </div>
                        </section>

                        <section className="mt-[69px] flex flex-col">
                          <div className="w-full flex flex-row items-center justify-between gap-[17px] mb-4">
                            <h2 className="ml-[10px] custom-text-[poppins-600-32px] text-[#141414]">
                              Avis d'utilisateurs
                            </h2>
                            <CustomToggle
                              label={values.feature_flag_testimonials ? "Show" : "Hide"}
                              checked={values.feature_flag_testimonials}
                              onChange={() => setFieldValue("feature_flag_testimonials", !values.feature_flag_testimonials)}
                            />
                          </div>
                          <div className="w-full border-b-solid border-b-[1px] border-b-[rgba(0,0,0,0.25)] mb-6"></div>
                          <div className="ml-[10px] flex flex-col w-full mt-[21px] gap-[24px]">
                            <div className="flex flex-row justify-between w-[410px]">
                              <p className="custom-text-[poppins-400-14px] text-[#6B7280]">Aucun avis pour le moment.</p>
                            </div>
                            <div className="w-full max-w-[600px]">
                              <p className="text-gray-500 custom-text-[poppins-400-14px]">
                                📢 Encouragez vos utilisateurs en leur envoyant une demande d'avis.<br />
                                <Link href="#" onClick={(e) => {
                                  e.preventDefault();
                                  navigator.clipboard.writeText(`${process.env.REACT_APP_LANDING_URL}/mentors/${mentorQuery.data.profile_id}`)
                                }} className="!text-[#407EC9] !underline !font-medium">Copier le lien</Link>
                                <span className="text-gray-500"> 🔗</span> et obtenez rapidement des retours.
                              </p>
                            </div>
                          </div>
                        </section>

                        <section className="mt-[69px] flex flex-col">
                          <div className="w-full flex flex-row items-center justify-between gap-[17px] mb-4">
                            <h2 className="ml-[10px] custom-text-[poppins-600-32px] text-[#141414]">
                              Les valeurs ajoutées
                            </h2>
                            <CustomToggle
                              label={values.feature_flag_value_proposition ? "Show" : "Hide"}
                              checked={values.feature_flag_value_proposition}
                              onChange={() => setFieldValue("feature_flag_value_proposition", !values.feature_flag_value_proposition)}
                            />
                          </div>
                          <div className="w-full border-b-solid border-b-[1px] border-b-[rgba(0,0,0,0.25)] mb-6"></div>
                          <div className="ml-[10px] flex flex-col w-full mt-[21px] gap-[24px]">
                            <div className="space-y-6">
                              {mentorValuePropositionQuery.isLoading && <LoadingImage />}
                              {mentorValuePropositionQuery.isError && <p className="text-red-500">Error loading value propositions.</p>}
                              {mentorValuePropositionQuery.data?.map((valueProposition) => (
                                <ValuePropositionCard
                                  key={valueProposition.id}
                                  mentorId={mentorQuery.data.id}
                                  valueProposition={valueProposition}
                                />
                              ))}
                              {showAddValuePropositionForm && (
                                <ValuePropositionCard
                                  mentorId={mentorQuery.data.id}
                                  onCancel={handleToggleAddValuePropositionForm}
                                />
                              )}
                            </div>
                            {!showAddValuePropositionForm && (
                              <button
                                type="button"
                                onClick={handleToggleAddValuePropositionForm}
                                className="text-[#407EC9] underline font-medium custom-text-[poppins-500-16px] self-start mt-4 hover:text-[#3569A7]"
                              >
                                Ajouter une valeur ajoutée +
                              </button>
                            )}
                          </div>
                        </section>
                        <section className="mt-[69px] flex flex-col">
                          <div className="w-full flex flex-row items-center justify-between gap-[17px] mb-4">
                            <h2 className="ml-[10px] custom-text-[poppins-600-32px] text-[#141414]">
                              Accompagnement a proposer
                            </h2>
                            <CustomToggle
                              label={values.feature_flag_mentorship_services ? "Show" : "Hide"}
                              checked={values.feature_flag_mentorship_services}
                              onChange={() => setFieldValue("feature_flag_mentorship_services", !values.feature_flag_mentorship_services)}
                            />
                          </div>
                          <div className="w-full border-b-solid border-b-[1px] border-b-[rgba(0,0,0,0.25)] mb-6"></div>
                          <div className="ml-[10px] flex flex-col w-full mt-[21px] gap-[24px]">
                            <div className="space-y-6">
                              {mentorServicesQuery.isLoading && <LoadingImage />}
                              {mentorServicesQuery.isError && <p className="text-red-500">Error loading services.</p>}
                              {mentorServicesQuery.data?.map((service) => (
                                <MentorshipCard
                                  key={service.id}
                                  mentorId={mentorQuery.data.id}
                                  service={service}
                                  onCancel={handleCancelService}
                                />
                              ))}
                              {showAddServiceForm && (
                                <MentorshipCard
                                  mentorId={mentorQuery.data.id}
                                  service={null}
                                  onCancel={handleCancelService}
                                />
                              )}
                            </div>
                            {!showAddServiceForm && (
                              <button
                                type="button"
                                onClick={handleToggleAddServiceForm}
                                className="text-[#407EC9] underline font-medium custom-text-[poppins-500-16px] self-start mt-4 hover:text-[#3569A7]"
                              >
                                Ajouter un Accompagnement +
                              </button>
                            )}
                          </div>
                        </section>

                        <section className="mt-[69px] flex flex-col">
                          <div className="w-full flex flex-row items-center justify-between gap-[17px] mb-4">
                            <h2 className="ml-[10px] custom-text-[poppins-600-32px] text-[#141414]">
                              Derniers Labs
                            </h2>
                            <CustomToggle
                              label={values.feature_flag_lab_catalogue ? "Show" : "Hide"}
                              checked={values.feature_flag_lab_catalogue}
                              onChange={() => setFieldValue("feature_flag_lab_catalogue", !values.feature_flag_lab_catalogue)}
                            />
                          </div>
                          <div className="w-full border-b-solid border-b-[1px] border-b-[rgba(0,0,0,0.25)] mb-6"></div>
                          <div className="ml-[10px] flex flex-col w-full mt-[21px] gap-[24px]">
                            <Link href="#" className="!text-[#407EC9] !underline !font-medium custom-text-[poppins-500-16px]">
                              Créer votre premier Lab +
                            </Link>
                          </div>
                        </section>

                      </Form>
                    )
                  }}
                </Formik>
              ) : (
                <div className="flex justify-center items-center w-full h-full">
                  <Link href={`${process.env.REACT_APP_LANDING_URL}/join-us-as-mentor#mentor-onboarding-form-section`} className="!rounded-lg !bg-[#407EC9] px-6 py-3 !text-white transition-colors !no-underline !custom-text-[poppins-500-15px] hover:!bg-[#3569A7]">
                    Become a Mentor
                  </Link>
                </div>
              )}
              {(isLoggedIn && user && mentorQuery.data && mentorQuery.data.id) && (
                <>

                  <section className="mt-[69px] flex flex-col">
                    <div className="w-full flex flex-row items-center justify-between gap-[17px] mb-4">
                      <h2 className="ml-[10px] custom-text-[poppins-600-32px] text-[#141414]">
                        Inviting users to become mentors
                      </h2>
                      <DarkCustomToggle
                        label={true ? "Show" : "Hide"}
                        checked={true}
                        onChange={() => setShowInviteMentors(!showInviteMentors)}
                      />
                    </div>
                    <div className="w-full border-b-solid border-b-[1px] border-b-[rgba(0,0,0,0.25)] mb-6"></div>
                    <div className="ml-[10px] flex flex-col w-full mt-[21px] gap-[24px]">
                      <p className="custom-text-[poppins-400-14px] text-[#6B7280]">Coming soon.</p>
                    </div>
                  </section>

                  <section className="mt-[69px] flex flex-col mb-10">
                    <div className="w-full flex flex-row items-center justify-between gap-[17px] mb-4">
                      <h2 className="ml-[10px] custom-text-[poppins-600-32px] text-[#141414]">
                        Legal information of copyright of mentor content
                      </h2>
                      <DarkCustomToggle
                        label={true ? "Show" : "Hide"}
                        checked={true}
                        onChange={() => setShowLegalInfo(!showLegalInfo)}
                      />
                    </div>
                    <div className="w-full border-b-solid border-b-[1px] border-b-[rgba(0,0,0,0.25)] mb-6"></div>
                    <div className="ml-[10px] flex flex-col w-full mt-[21px] gap-[24px]">
                      <p className="custom-text-[poppins-400-14px] text-[#6B7280]">Coming soon.</p>
                    </div>
                  </section>
                </>
              )}
            </div>
          </>
          )
      }
    </section>
  );
};

export default EditMentorProfilePage;
